import { faPause, faPencil, faPlay, faSync, faTrash } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import {
    ACTIVATE_ENTRY,
    BAZAAR_NOTES_ADD_NOTE,
    BAZAAR_NOTES_TO_NOTES,
    BZ2_PRODUCT_DELETE_CONFIRM,
    CHANGE_ENTRY,
    DELETE_ENTRY,
    INACTIVATE_ENTRY,
    REPOST_ENTRY,
    SELLER_ACTION_BUTTONS_UNDELETE,
    SELLER_ACTION_BUTTONS_UNDELETE_CONFIRM,
    YOU_HAVE_NO_MORE_REPOSTS_SHORT,
} from 'mk/autogenerated/translations/SellerActionButtons.e6608bb2de8bc7d08104bf1fe2153e68'
import { slugToArray } from 'mk/bazaar/product/utils';
import {
    adminclub_bazaar_product_url,
    adminclub_users_url,
    adminclub_user_notes_url,
    bazaar_product_history_url,
    bazaar_single_page_modify_product_url,
} from 'mk/urls/functions';
import DeletedReasonForm, { DeletedReasonFormData } from 'mk2/apps/bazaar/components/DeletedReasonForm';
import { DeletedReason, ProductEntity, RepostableStatus } from 'mk2/apps/bazaar/schemas';
import { NoteEntity } from 'mk2/apps/notes/schemas';
import { Btn, BtnType } from 'mk2/components/Btn';
import { Link } from 'mk2/components/Link';
import { Panel } from 'mk2/components/Panel';
import useEventCallback from 'mk2/hooks/useEventCallback';
import React, { useState } from 'react';
import styles from './SellerActionButtons.mscss';

interface OwnProps {
    className?: string;
    product: ProductEntity;
    isSeller?: boolean;
    isAdmin?: boolean;
    isModerator?: boolean;
    lastNote?: NoteEntity;
    historyCount?: number;

    isActivating?: boolean;
    isDeactivating?: boolean;
    isDeleting?: boolean;
    isUndeleting?: boolean;
    isReposting?: boolean;

    onActivate(event: React.MouseEvent<HTMLAnchorElement>);
    onDeactivate(event: React.MouseEvent<HTMLAnchorElement>);
    onDelete(reason?: DeletedReason);
    onUndelete();
    onRepost(event: React.MouseEvent<HTMLAnchorElement>);
}

type Props = OwnProps;

const SellerActionButtons: React.FunctionComponent<Props> = ({
    className,
    product,
    isSeller = false,
    isAdmin = false,
    isModerator = false,
    lastNote = null,
    historyCount = 0,
    onActivate,
    onDeactivate,
    onRepost,
    onDelete,
    onUndelete,
    isActivating = false,
    isDeactivating = false,
    isDeleting = false,
    isReposting = false,
    isUndeleting = false,
}) => {
    const [showDeletedReasons, setShowDeletedReasons] = useState<boolean>(false);

    const onDeleteCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if ((isAdmin || isModerator) && !isSeller) {
                setShowDeletedReasons(true);
            } else {
                if (!confirm(BZ2_PRODUCT_DELETE_CONFIRM)) {
                    return;
                }

                onDelete();
            }
        },
        [onDelete, isAdmin, isModerator, setShowDeletedReasons],
    );

    const onUndeleteCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (!confirm(SELLER_ACTION_BUTTONS_UNDELETE_CONFIRM)) {
                return;
            }
            onUndelete();
        },
        [onUndelete],
    );

    const onCancelDeleteForm = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            setShowDeletedReasons(false);
        },
        [setShowDeletedReasons],
    );

    const onSaveDeletedReason = useEventCallback(
        (values: DeletedReasonFormData) => {
            onDelete(values.reason);
            setShowDeletedReasons(false);
        },
        [onDelete, setShowDeletedReasons],
    );

    const adminActions = (
        <>
            {historyCount > 0 && (
                <Link
                    className={styles.SellerActionButtons__history}
                    to={bazaar_product_history_url(product.id)}
                    target="_blank"
                >
                    History ({historyCount} changes)
                </Link>
            )}
            {isAdmin && (
                <Link
                    className={styles.SellerActionButtons__adminclub}
                    to={adminclub_bazaar_product_url(product.id)}
                    target="_blank"
                >
                    Adminclub (product)
                </Link>
            )}
            {isAdmin && product.seller?.username && (
                <Link
                    className={styles.SellerActionButtons__adminclub}
                    to={adminclub_users_url({ user: product.seller.username })}
                    target="_blank"
                >
                    Adminclub (user)
                </Link>
            )}
        </>
    );

    if (product.state.isDeleted) {
        return isAdmin ? (
            <Panel
                className={cx(styles.SellerActionButtons, className, isAdmin && styles['SellerActionButtons--admin'])}
            >
                {new Date(product.endTime).getTime() > Date.now() - 24 * 60 * 60 * 1000 && (
                    <div className={styles.SellerActionButtons__top}>
                        <Btn
                            className={styles.SellerActionButtons__top__button}
                            type={BtnType.AdminOutline}
                            icon={faTrash}
                            onClick={onUndeleteCallback}
                            hasSpinner={isUndeleting}
                            label={SELLER_ACTION_BUTTONS_UNDELETE}
                        />
                    </div>
                )}
                {adminActions}
            </Panel>
        ) : null;
    }

    const category = product.properties.find((p) => p[0] === 'cat')[1];

    return (
        <Panel
            className={cx(
                styles.SellerActionButtons,
                className,
                isAdmin && styles['SellerActionButtons--admin'],
                isModerator && styles['SellerActionButtons--moderator'],
            )}
        >
            <div className={styles.SellerActionButtons__top}>
                <Btn
                    className={styles.SellerActionButtons__top__button}
                    type={BtnType.BlueOutline}
                    icon={faPencil}
                    label={CHANGE_ENTRY}
                    link={bazaar_single_page_modify_product_url.apply(null, slugToArray(product.slug))}
                />{' '}
                {(isSeller || isAdmin) &&
                    !product.seller?.hasBlockedBazaar &&
                    (product.state.isInactive || product.state.isActive) && (
                        <>
                            <Btn
                                className={styles.SellerActionButtons__top__button}
                                type={BtnType.BlueOutline}
                                icon={product.state.isActive ? faPause : faPlay}
                                onClick={product.state.isActive ? onDeactivate : onActivate}
                                label={product.state.isActive ? INACTIVATE_ENTRY : ACTIVATE_ENTRY}
                                hasSpinner={product.state.isActive ? isDeactivating : isActivating}
                            />{' '}
                        </>
                    )}
                <Btn
                    className={styles.SellerActionButtons__top__button}
                    type={BtnType.BlueOutline}
                    icon={faTrash}
                    onClick={onDeleteCallback}
                    hasSpinner={isDeleting}
                    label={
                        <>
                            {DELETE_ENTRY}
                            {!isSeller && <>&hellip;</>}
                        </>
                    }
                />
            </div>

            {(isAdmin || isModerator) && product.seller &&
                (lastNote ? (
                    <div className={styles.SellerActionButtons__notes}>
                        {lastNote.note}

                        <Link
                            className={styles.SellerActionButtons__notes__change}
                            to={
                                isAdmin
                                    ? adminclub_users_url({ user: product.seller.username })
                                    : adminclub_user_notes_url({ user: product.seller.username })
                            }
                            target="_blank"
                        >
                            {BAZAAR_NOTES_TO_NOTES}
                        </Link>
                    </div>
                ) : (
                    <Link
                        className={styles.SellerActionButtons__no_notes}
                        to={
                            isAdmin
                                ? adminclub_users_url({ user: product.seller.username })
                                : adminclub_user_notes_url({ user: product.seller.username })
                        }
                        target="_blank"
                    >
                        {BAZAAR_NOTES_ADD_NOTE}
                    </Link>
                ))}

            {isSeller &&
                !product.seller?.hasBlockedBazaar &&
                product.repostableStatus === RepostableStatus.REPOSTABLE_FREE && (
                    <div className={styles.SellerActionButtons__repost}>
                        <Btn
                            className={styles.SellerActionButtons__repost__button}
                            type={BtnType.Blue}
                            icon={faSync}
                            onClick={onRepost}
                            disabled={!product.seller?.availableReposts}
                            label={product.seller?.availableReposts ? REPOST_ENTRY : YOU_HAVE_NO_MORE_REPOSTS_SHORT}
                            hasSpinner={isReposting}
                        />
                    </div>
                )}

            {showDeletedReasons && (
                <DeletedReasonForm
                    onCancel={onCancelDeleteForm}
                    onSave={onSaveDeletedReason}
                    alreadyDeleted={product.state.isDeleted}
                />
            )}

            {adminActions}
        </Panel>
    );
};

SellerActionButtons.displayName = 'SellerActionButtons';

export default React.memo<Props>(SellerActionButtons);
